import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/LanguageContext';

const getSubOptions = (t) => [
  { value: 'active',    label: t('userManager.subActive'),   color: '#0d9488' },
  { value: 'trial',     label: t('userManager.subTrial'),  color: '#d97706' },
  { value: 'inactive',  label: t('userManager.subInactive'),   color: '#64748b' },
  { value: 'cancelled', label: t('userManager.subCancelled'),   color: '#dc2626' },
];

function subOpt(s, SUB_OPTIONS) { return SUB_OPTIONS.find(o => o.value === s) || SUB_OPTIONS[2]; }

function daysLeft(end) {
  if (!end) return null;
  return Math.ceil((new Date(end) - new Date()) / 86400000);
}

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function toDateInput(d) {
  if (!d) return '';
  return new Date(d).toISOString().split('T')[0];
}

const EMPTY_EDIT = {
  name: '', subscription_status: 'inactive',
  subscription_start: '', subscription_end: '',
  is_active: true, notes: '', new_password: '',
};

// Admin bir kullanıcının OKX kimlik bilgilerini ASLA göremez/değiştiremez — bu panel SADECE
// bilgi amaçlı: hangi platform (Base URL) seçili ve bağlantı gerçekten çalışıyor mu (Test Et,
// canlı bakiye sorgusu). bkz. backend/src/routes/adminUsers.js.
const BASE_URL_LABELS = { 'https://www.okx.com': 'OKX Global', 'https://tr.okx.com': 'OKX TR' };

// ── Sub/Active badges ──────────────────────────────────────────

function SubBadge({ status }) {
  const { t } = useTranslation();
  const opt = subOpt(status, getSubOptions(t));
  return (
    <span className="um-badge" style={{ background: opt.color + '1a', color: opt.color, borderColor: opt.color + '55' }}>
      {opt.label}
    </span>
  );
}

function ActiveBadge({ active }) {
  const { t } = useTranslation();
  return active === false
    ? <span className="um-badge um-badge-blocked">{t('userManager.blocked')}</span>
    : null;
}

// ── Edit View ──────────────────────────────────────────────────

function EditView({ user, token, onBack, onSaved }) {
  const { t } = useTranslation();
  const SUB_OPTIONS = getSubOptions(t);
  const [form,    setForm]    = useState({ ...EMPTY_EDIT, ...user, new_password: '', subscription_start: toDateInput(user.subscription_start), subscription_end: toDateInput(user.subscription_end) });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [showPw,  setShowPw]  = useState(false);
  const [okxBaseUrl,    setOkxBaseUrl]    = useState('https://www.okx.com'); // salt bilgi — düzenlenemez
  const [testResult,    setTestResult]    = useState(null); // { status: 'valid'|'invalid'|'missing' } | null
  const [testing,       setTesting]       = useState(false);

  const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const days   = daysLeft(form.subscription_end);

  // Kullanıcının aktif borsasını (salt okunur) yükle
  useEffect(() => {
    fetch(`/api/admin/users/${user.id}/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.okx_base_url) setOkxBaseUrl(d.okx_base_url); })
      .catch(() => {});
  }, [user.id, token]);

  const handleTestConnection = () => {
    setTesting(true);
    setTestResult(null);
    fetch(`/api/admin/users/${user.id}/exchange-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setTestResult(d))
      .catch(() => setTestResult({ status: 'invalid' }))
      .finally(() => setTesting(false));
  };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const body = { ...form };
      delete body.new_password;
      const r = await fetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).error);

      if (form.new_password) {
        const r2 = await fetch(`/api/admin/users/${user.id}/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ password: form.new_password }),
        });
        if (!r2.ok) throw new Error((await r2.json()).error);
      }

      setMsg({ text: t('userManager.saved'), type: 'ok' });
      setTimeout(() => onSaved(), 1000);
    } catch (err) {
      setMsg({ text: err.message, type: 'error' });
    }
    setSaving(false);
  };

  return (
    <div className="um-edit-view">
      <div className="um-edit-header">
        <button className="um-back-btn" onClick={onBack}>{t('userManager.back')}</button>
        <div>
          <div className="um-edit-name">{user.name || '—'}</div>
          <div className="um-edit-email">{user.email}</div>
        </div>
      </div>

      <div className="um-edit-grid">

        <label className="settings-label">{t('userManager.fullName')}</label>
        <input className="settings-input" value={form.name} onChange={e => set('name', e.target.value)} />

        <label className="settings-label">{t('userManager.subscriptionStatus')}</label>
        <select className="settings-input" value={form.subscription_status} onChange={e => set('subscription_status', e.target.value)}>
          {SUB_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <label className="settings-label">{t('userManager.subStart')}</label>
        <input className="settings-input" type="date" value={form.subscription_start} onChange={e => set('subscription_start', e.target.value)} />

        <label className="settings-label">
          {t('userManager.subEnd')}
          {days !== null && (
            <span className={`um-days-tag ${days <= 0 ? 'expired' : days < 7 ? 'soon' : ''}`}>
              {days <= 0 ? t('userManager.expired') : t('userManager.daysLeft', { n: days })}
            </span>
          )}
        </label>
        <input className="settings-input" type="date" value={form.subscription_end} onChange={e => set('subscription_end', e.target.value)} />

        <label className="settings-label">{t('userManager.accountStatus')}</label>
        <div className="um-status-toggle">
          <button type="button" className={`um-status-btn${form.is_active ? ' is-active' : ''}`} onClick={() => set('is_active', true)}>{t('userManager.active')}</button>
          <button type="button" className={`um-status-btn${!form.is_active ? ' is-blocked' : ''}`} onClick={() => set('is_active', false)}>{t('userManager.blockedBtn')}</button>
        </div>

        <label className="settings-label">{t('userManager.notes')}</label>
        <textarea
          className="settings-input"
          rows={2}
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder={t('userManager.notesPlaceholder')}
          style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
        />

        <label className="settings-label">{t('userManager.newPassword')}</label>
        <div className="settings-input-row">
          <input
            className="settings-input"
            type={showPw ? 'text' : 'password'}
            value={form.new_password}
            onChange={e => set('new_password', e.target.value)}
            placeholder={t('userManager.changePlaceholder')}
          />
          <button className="settings-eye" type="button" onClick={() => setShowPw(s => !s)}>
            {showPw ? '🙈' : '👁'}
          </button>
        </div>

        <label className="settings-label" style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 12, gridColumn: '1 / -1' }}>{t('userManager.okxApiKeysHeader')}</label>
        <p className="settings-hint" style={{ gridColumn: '1 / -1', margin: '-6px 0 4px' }}>{t('userManager.okxInfoOnly')}</p>

        <label className="settings-label">{t('userManager.baseUrl')}</label>
        <div className="settings-input-row">
          <input className="settings-input" value={BASE_URL_LABELS[okxBaseUrl] || okxBaseUrl} readOnly disabled />
          <button className="btn-refresh" type="button" onClick={handleTestConnection} disabled={testing}>
            {testing ? t('userManager.testing') : t('userManager.testConnection')}
          </button>
        </div>
        {testResult && (
          <p className="settings-hint" style={{ gridColumn: '1 / -1', margin: 0, color: testResult.status === 'valid' ? 'var(--accent)' : '#dc2626' }}>
            {testResult.status === 'valid'   && t('userManager.testValid')}
            {testResult.status === 'invalid' && t('userManager.testInvalid')}
            {testResult.status === 'missing' && t('userManager.testMissing')}
          </p>
        )}

      </div>

      <div className="settings-footer" style={{ marginTop: 8 }}>
        {msg && <span className={`settings-msg settings-msg-${msg.type}`}>{msg.text}</span>}
        {/* İki adımlı doğrulamayı sıfırla (madde 12) — telefonunu kaybeden kullanıcı için; bir sonraki
            girişte (politika zorunluysa) yeniden kurar. */}
        <button
          className="btn ts-btn-inline"
          type="button"
          onClick={async () => {
            if (!window.confirm(t('tfa.resetConfirm'))) return;
            const r = await fetch(`/api/admin/security/users/${user.id}/reset-2fa`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
            const d = await r.json().catch(() => ({}));
            setMsg(r.ok ? { text: t('tfa.resetDone'), type: 'ok' } : { text: d.error || d.message || t('userManager.saveError'), type: 'error' });
          }}
        >
          {t('tfa.reset')}
        </button>
        <button className="btn btn-start" style={{ minWidth: 120 }} onClick={handleSave} disabled={saving}>
          {saving ? t('userManager.saving') : t('userManager.save')}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────

export default function UserManager({ token, globalForm, onGlobalChange }) {
  const { t } = useTranslation();
  const SUB_OPTIONS = getSubOptions(t);
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [view,         setView]         = useState('list');
  const [editing,      setEditing]      = useState(null);
  const [selected,     setSelected]     = useState(new Set());
  const [search,       setSearch]       = useState('');
  const [filterSub,    setFilterSub]    = useState('');  // '' = all
  const [filterActive, setFilterActive] = useState('');  // '' | 'active' | 'blocked'
  const [confirmDel,   setConfirmDel]   = useState(null); // null | 'single' | 'bulk'
  const [bulkSub,      setBulkSub]      = useState('');
  const [showSysCreds, setShowSysCreds] = useState(false);
  const [showPass,     setShowPass]     = useState({});
  const [savingSys,    setSavingSys]    = useState(false);
  const [sysMsg,       setSysMsg]       = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/users', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setUsers(await r.json());
    } catch (_) {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Filtreleme ─────────────────────────────────────────────
  const filtered = users.filter(u => {
    if (filterSub && u.subscription_status !== filterSub) return false;
    if (filterActive === 'active'  && u.is_active === false) return false;
    if (filterActive === 'blocked' && u.is_active !== false) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ── Seçim ─────────────────────────────────────────────────
  const allSelected = filtered.length > 0 && filtered.every(u => selected.has(u.id));
  const toggleAll  = () => {
    if (allSelected) {
      setSelected(s => { const n = new Set(s); filtered.forEach(u => n.delete(u.id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); filtered.forEach(u => n.add(u.id)); return n; });
    }
  };
  const toggleOne = (id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const selectedCount = selected.size;
  const selectedIds   = [...selected];

  // ── Toplu İşlemler ────────────────────────────────────────
  const bulkAction = async (action) => {
    const ids = selectedIds;
    try {
      if (action === 'delete') {
        await fetch('/api/admin/users/bulk/delete', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids }),
        });
      } else if (action === 'activate') {
        await fetch('/api/admin/users/bulk/update', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids, updates: { is_active: true } }),
        });
      } else if (action === 'block') {
        await fetch('/api/admin/users/bulk/update', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids, updates: { is_active: false } }),
        });
      } else if (action === 'sub' && bulkSub) {
        await fetch('/api/admin/users/bulk/update', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids, updates: { subscription_status: bulkSub } }),
        });
        setBulkSub('');
      }
      setSelected(new Set());
      setConfirmDel(null);
      fetchUsers();
    } catch (_) {}
  };

  // ── Tekil Silme ────────────────────────────────────────────
  const deleteSingle = async (id) => {
    await fetch(`/api/admin/users/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setConfirmDel(null);
    fetchUsers();
  };

  // ── Sistem Bilgileri Kaydet ────────────────────────────────
  const saveSys = async () => {
    setSavingSys(true); setSysMsg(null);
    try {
      const r = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          admin_username:  globalForm.admin_username,
          admin_password:  globalForm.admin_password,
          viewer_username: globalForm.viewer_username,
          viewer_password: globalForm.viewer_password,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || '');
      setSysMsg({ text: t('userManager.saved'), type: 'ok' });
      setTimeout(() => setSysMsg(null), 3000);
    } catch (err) {
      setSysMsg({ text: err.message || t('userManager.saveError'), type: 'error' });
    }
    setSavingSys(false);
  };

  // ── Edit View ──────────────────────────────────────────────
  if (view === 'edit' && editing) {
    return (
      <EditView
        user={editing}
        token={token}
        onBack={() => { setView('list'); setEditing(null); }}
        onSaved={() => { setView('list'); setEditing(null); fetchUsers(); }}
      />
    );
  }

  // ── List View ──────────────────────────────────────────────
  return (
    <div className="um-root">

      {/* Confirm dialog */}
      {confirmDel && (
        <div className="um-confirm-overlay">
          <div className="um-confirm-box">
            <div className="um-confirm-title">
              {confirmDel.type === 'bulk'
                ? t('userManager.confirmDeleteBulk', { n: confirmDel.ids.length })
                : t('userManager.confirmDeleteSingle', { email: confirmDel.user?.email })}
            </div>
            <div className="um-confirm-sub">{t('userManager.confirmDeleteSub')}</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn" style={{ flex: 1 }} onClick={() => setConfirmDel(null)}>{t('userManager.cancel')}</button>
              <button
                className="btn um-btn-danger"
                style={{ flex: 1 }}
                onClick={() => confirmDel.type === 'bulk'
                  ? bulkAction('delete')
                  : deleteSingle(confirmDel.user.id)}
              >
                {t('userManager.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sistem Erişimi (collapsible) */}
      <div className="um-sys-section">
        <button className="um-sys-toggle" onClick={() => setShowSysCreds(s => !s)}>
          <span>{t('userManager.systemAccessTitle')}</span>
          <span>{showSysCreds ? '▲' : '▼'}</span>
        </button>
        {showSysCreds && (
          <div className="um-sys-body">
            {[
              ['admin_username',  t('userManager.adminUsername'),   false],
              ['admin_password',  t('userManager.adminPassword'),       true],
              ['viewer_username', t('userManager.viewerUsername'),  false],
              ['viewer_password', t('userManager.viewerPassword'),      true],
            ].map(([key, label, secret]) => (
              <div className="settings-field" key={key} style={{ gap: 3 }}>
                <label className="settings-label">{label}</label>
                <div className="settings-input-row">
                  <input
                    className="settings-input"
                    type={secret && !showPass[key] ? 'password' : 'text'}
                    value={globalForm[key] ?? ''}
                    placeholder={secret ? t('userManager.changePlaceholder') : ''}
                    onChange={e => onGlobalChange(key, e.target.value)}
                  />
                  {secret && (
                    <button className="settings-eye" type="button"
                      onClick={() => setShowPass(s => ({ ...s, [key]: !s[key] }))}>
                      {showPass[key] ? '🙈' : '👁'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
              {sysMsg && <span className={`settings-msg settings-msg-${sysMsg.type}`}>{sysMsg.text}</span>}
              <button className="btn btn-start" style={{ minWidth: 96 }} onClick={saveSys} disabled={savingSys}>
                {savingSys ? '…' : t('userManager.save')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="um-list-header">
        <span className="um-list-title">{t('userManager.registeredUsers')} <span className="um-count">({filtered.length}/{users.length})</span></span>
        <button className="um-refresh-btn" onClick={fetchUsers} title={t('userManager.refresh')}>↺</button>
      </div>

      {/* Filtreler */}
      <div className="um-filters">
        <input
          className="um-search"
          placeholder={t('userManager.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="um-filter-group">
          <button className={`um-filter-btn${filterSub === '' ? ' active' : ''}`} onClick={() => setFilterSub('')}>{t('userManager.all')}</button>
          {SUB_OPTIONS.map(o => (
            <button
              key={o.value}
              className={`um-filter-btn${filterSub === o.value ? ' active' : ''}`}
              style={filterSub === o.value ? { borderColor: o.color, color: o.color, background: o.color + '15' } : {}}
              onClick={() => setFilterSub(filterSub === o.value ? '' : o.value)}
            >
              {o.label}
            </button>
          ))}
          <span className="um-filter-sep">|</span>
          <button className={`um-filter-btn${filterActive === 'active' ? ' active' : ''}`} onClick={() => setFilterActive(filterActive === 'active' ? '' : 'active')}>{t('userManager.subActive')}</button>
          <button className={`um-filter-btn${filterActive === 'blocked' ? ' active' : ''}`} onClick={() => setFilterActive(filterActive === 'blocked' ? '' : 'blocked')}>{t('userManager.blocked')}</button>
        </div>
      </div>

      {/* Toplu işlem çubuğu */}
      {selectedCount > 0 && (
        <div className="um-bulk-bar">
          <span className="um-bulk-count">{t('userManager.selectedCount', { n: selectedCount })}</span>
          <button className="um-bulk-btn" onClick={() => bulkAction('activate')}>{t('userManager.activateBulk')}</button>
          <button className="um-bulk-btn" onClick={() => bulkAction('block')}>{t('userManager.blockBulk')}</button>
          <select
            className="um-bulk-select"
            value={bulkSub}
            onChange={e => setBulkSub(e.target.value)}
          >
            <option value="">{t('userManager.changeSubscription')}</option>
            {SUB_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {bulkSub && (
            <button className="um-bulk-btn um-bulk-apply" onClick={() => bulkAction('sub')}>{t('userManager.apply')}</button>
          )}
          <button
            className="um-bulk-btn um-bulk-del"
            onClick={() => setConfirmDel({ type: 'bulk', ids: selectedIds })}
          >
            {t('userManager.deleteBulk')}
          </button>
          <button className="um-bulk-clear" onClick={() => setSelected(new Set())}>{t('userManager.clearSelection')}</button>
        </div>
      )}

      {/* Tablo başlığı */}
      {!loading && filtered.length > 0 && (
        <div className="um-table-head">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="um-checkbox" />
          <span>{t('userManager.colUser')}</span>
          <span>{t('userManager.colSubscription')}</span>
          <span>{t('userManager.colEnd')}</span>
          <span></span>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="um-empty">{t('userManager.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="um-empty">{users.length === 0 ? t('userManager.noUsers') : t('userManager.noFilterMatch')}</div>
      ) : (
        <div className="um-table">
          {filtered.map(u => {
            const days  = daysLeft(u.subscription_end);
            const isExp = days !== null && days <= 0;
            const isSel = selected.has(u.id);
            return (
              <div key={u.id} className={`um-row${isSel ? ' um-row-sel' : ''}${u.is_active === false ? ' um-row-blocked' : ''}`}>
                <input type="checkbox" checked={isSel} onChange={() => toggleOne(u.id)} className="um-checkbox" />
                <div className="um-row-info">
                  <div className="um-row-name">
                    {u.name || '—'}
                    <span className="um-user-id">#{u.id}</span>
                    {u.is_active === false && <ActiveBadge active={false} />}
                  </div>
                  <div className="um-row-email">{u.email}</div>
                  {u.notes && <div className="um-row-notes" title={u.notes}>{u.notes}</div>}
                </div>
                <div className="um-row-sub">
                  <SubBadge status={u.subscription_status} />
                </div>
                <div className="um-row-dates">
                  {u.subscription_end ? (
                    <>
                      <span className={`um-days-sm${isExp ? ' expired' : days < 7 ? ' soon' : ''}`}>
                        {isExp ? t('userManager.expiredShort') : t('userManager.daysLeftShort', { n: days })}
                      </span>
                      <span className="um-end-dt">{fmt(u.subscription_end)}</span>
                    </>
                  ) : <span className="um-end-dt">—</span>}
                </div>
                <div className="um-row-actions">
                  <button className="um-action-btn um-edit-btn" title={t('userManager.editTitle')}
                    onClick={() => { setEditing(u); setView('edit'); }}>✎</button>
                  <button className="um-action-btn um-del-btn" title={t('userManager.deleteTitle')}
                    onClick={() => setConfirmDel({ type: 'single', user: u })}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
